export class MusicTheory {
    constructor() {
        this.A4_FREQUENCY = 440;
        
        this.noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        
        this.intervals = [
            { name: 'Unison', semitones: 0 },
            { name: 'Minor 2nd', semitones: 1 },
            { name: 'Major 2nd', semitones: 2 },
            { name: 'Minor 3rd', semitones: 3 },
            { name: 'Major 3rd', semitones: 4 },
            { name: 'Perfect 4th', semitones: 5 },
            { name: 'Tritone', semitones: 6 },
            { name: 'Perfect 5th', semitones: 7 },
            { name: 'Minor 6th', semitones: 8 },
            { name: 'Major 6th', semitones: 9 },
            { name: 'Minor 7th', semitones: 10 },
            { name: 'Major 7th', semitones: 11 },
            { name: 'Octave', semitones: 12 }
        ];
        
        this.scales = [
            { name: 'C Major', pattern: [0, 2, 4, 5, 7, 9, 11] },
            { name: 'G Major', pattern: [7, 9, 11, 0, 2, 4, 6] },
            { name: 'D Major', pattern: [2, 4, 6, 7, 9, 11, 1] },
            { name: 'A Minor', pattern: [9, 11, 0, 2, 4, 5, 7] },
            { name: 'E Minor', pattern: [4, 6, 7, 9, 11, 0, 2] },
            { name: 'C Pentatonic', pattern: [0, 2, 4, 7, 9] }
        ];
        
        this.rhythms = [
            { name: 'Whole Note', duration: 4 },
            { name: 'Half Note', duration: 2 },
            { name: 'Quarter Note', duration: 1 },
            { name: 'Eighth Note', duration: 0.5 },
            { name: 'Sixteenth Note', duration: 0.25 }
        ];
    }

    /**
     * Convert frequency to nearest note name
     */
    frequencyToNote(frequency) {
        if (!frequency || frequency <= 0) return null;
        
        // Calculate number of semitones from A4
        const semitones = 12 * Math.log2(frequency / this.A4_FREQUENCY);
        const noteIndex = Math.round(semitones) + 69; // MIDI note number
        
        // Convert to note name
        const octave = Math.floor(noteIndex / 12) - 1;
        const note = this.noteNames[noteIndex % 12];
        
        return `${note}${octave}`;
    }

    /**
     * Convert note name to frequency
     */
    noteToFrequency(noteName) {
        // Parse note name (e.g., "C4", "A#3")
        const match = noteName.match(/^([A-G]#?)(\d+)$/);
        if (!match) return null;
        
        const [, note, octaveStr] = match;
        const octave = parseInt(octaveStr);
        
        const noteIndex = this.noteNames.indexOf(note);
        if (noteIndex === -1) return null;
        
        // Calculate MIDI note number
        const midiNote = (octave + 1) * 12 + noteIndex;
        
        // Convert to frequency
        return this.A4_FREQUENCY * Math.pow(2, (midiNote - 69) / 12);
    }

    /**
     * Check if two notes are close (within 1 semitone)
     */
    isClose(note1, note2) {
        const freq1 = this.noteToFrequency(note1);
        const freq2 = this.noteToFrequency(note2);
        
        if (!freq1 || !freq2) return false;
        
        const semitones = Math.abs(12 * Math.log2(freq1 / freq2));
        return semitones <= 1;
    }

    /**
     * Get the cents difference between a frequency and target note
     * (100 cents = 1 semitone)
     */
    getCentsOff(frequency, targetNote) {
        const targetFreq = this.noteToFrequency(targetNote);
        if (!targetFreq) return null;
        
        return 1200 * Math.log2(frequency / targetFreq);
    }

    /**
     * Get random note for practice
     */
    getRandomNote() {
        const notes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
        const octaves = [3, 4, 5];
        
        const note = notes[Math.floor(Math.random() * notes.length)];
        const octave = octaves[Math.floor(Math.random() * octaves.length)];
        
        return note + octave;
    }

    /**
     * Get intervals for practice
     */
    getIntervals() {
        return this.intervals;
    }

    /**
     * Get scales for practice
     */
    getScales() {
        return this.scales;
    }

    /**
     * Get rhythms for practice
     */
    getRhythms() {
        return this.rhythms;
    }

    /**
     * Calculate interval between two notes
     */
    getInterval(note1, note2) {
        const freq1 = this.noteToFrequency(note1);
        const freq2 = this.noteToFrequency(note2);
        
        if (!freq1 || !freq2) return null;
        
        const semitones = Math.round(12 * Math.log2(freq2 / freq1));
        const normalizedSemitones = ((semitones % 12) + 12) % 12;
        
        return this.intervals.find(i => i.semitones === normalizedSemitones);
    }

    /**
     * Generate scale notes from a root note
     */
    getScaleNotes(rootNote, scalePattern) {
        const rootFreq = this.noteToFrequency(rootNote);
        if (!rootFreq) return null;
        
        return scalePattern.map(semitones => {
            const freq = rootFreq * Math.pow(2, semitones / 12);
            return this.frequencyToNote(freq);
        });
    }
}
